import React, { useState, useEffect, useContext } from "react";
import { Link2 } from "lucide-react";
import { WorkspaceContext } from "../context/WorkspaceContext";
import axios from "axios";

import { API_BASE } from "../config/apiConfig";

export default function Requirements() {
  const {
    activeRepoProject,
    setActiveRepoProject,
    testCaseRepo
  } = useContext(WorkspaceContext);

  const [requirements, setRequirements] = useState([]);
  const [searchRequirementsQuery, setSearchRequirementsQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // Table sorting states
  const [sortField, setSortField] = useState("");
  const [sortAsc, setSortAsc] = useState(true);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  const getSortedRequirements = () => {
    let list = [...requirements];
    if (searchRequirementsQuery) {
      const q = searchRequirementsQuery.toLowerCase();
      list = list.filter(r =>
        (r.title && r.title.toLowerCase().includes(q)) ||
        (r.description && r.description.toLowerCase().includes(q)) ||
        (r.req_id && r.req_id.toLowerCase().includes(q)) ||
        (r.priority && r.priority.toLowerCase().includes(q)) ||
        (r.compliance_status && r.compliance_status.toLowerCase().includes(q))
      );
    }
    if (!sortField) return list;
    return list.sort((a, b) => {
      let valA = a[sortField];
      let valB = b[sortField];

      if (sortField === "mapped_tests") {
        valA = (a.mapped_tests || []).length;
        valB = (b.mapped_tests || []).length;
      }

      if (typeof valA === "string") valA = valA.toLowerCase();
      if (typeof valB === "string") valB = valB.toLowerCase();

      if (valA === valB) return 0;
      if (valA === null || valA === undefined) return 1;
      if (valB === null || valB === undefined) return -1;

      const comp = valA < valB ? -1 : 1;
      return sortAsc ? comp : -comp;
    });
  };

  const renderSortIndicator = (field) => {
    if (sortField !== field) return <span style={{ fontSize: "10px", color: "#94a3b8" }}> ⇅</span>;
    return <span style={{ fontSize: "10px", color: "var(--primary)" }}>{sortAsc ? " ▲" : " ▼"}</span>;
  };

  // Modals state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);

  // Form states
  const [selectedReq, setSelectedReq] = useState(null);
  const [reqTitle, setReqTitle] = useState("");
  const [reqDesc, setReqDesc] = useState("");
  const [reqPriority, setReqPriority] = useState("Medium");

  // Mapping state
  const [mappedTests, setMappedTests] = useState([]);

  // Fetch requirement compliance status
  const fetchRequirements = async () => {
    if (!activeRepoProject) return;
    setLoading(true);
    setErrorMsg("");
    try {
      const res = await axios.get(`${API_BASE}/api/requirements/status?project=${activeRepoProject}`);
      if (res.data.success) {
        setRequirements(res.data.status);
      } else {
        setErrorMsg(res.data.error || "Failed to load requirements.");
      }
    } catch (err) {
      setErrorMsg("Failed to connect to backend api.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequirements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRepoProject]);

  // Toast handler helper
  const triggerToast = (msg, isSuccess = true) => {
    if (isSuccess) {
      setSuccessMsg(msg);
      setTimeout(() => setSuccessMsg(""), 3000);
    } else {
      setErrorMsg(msg);
      setTimeout(() => setErrorMsg(""), 4000);
    }
  };

  // Add requirement
  const handleAddRequirement = async (e) => {
    e.preventDefault();
    if (!reqTitle.trim()) return;
    try {
      const res = await axios.post(`${API_BASE}/api/requirements`, {
        project: activeRepoProject,
        title: reqTitle.trim(),
        description: reqDesc.trim(),
        priority: reqPriority
      });
      if (res.data.success) {
        triggerToast(`Created requirement ${res.data.req_id} successfully!`);
        setShowAddModal(false);
        setReqTitle("");
        setReqDesc("");
        setReqPriority("Medium");
        fetchRequirements();
      } else {
        triggerToast(res.data.error, false);
      }
    } catch (err) {
      triggerToast("Error saving requirement.", false);
    }
  };

  // Edit requirement
  const handleEditRequirement = async (e) => {
    e.preventDefault();
    if (!selectedReq || !reqTitle.trim()) return;
    try {
      const res = await axios.put(`${API_BASE}/api/requirements/${selectedReq.req_id}`, {
        title: reqTitle.trim(),
        description: reqDesc.trim(),
        priority: reqPriority
      });
      if (res.data.success) {
        triggerToast("Requirement updated successfully!");
        setShowEditModal(false);
        setSelectedReq(null);
        setReqTitle("");
        setReqDesc("");
        fetchRequirements();
      } else {
        triggerToast(res.data.error, false);
      }
    } catch (err) {
      triggerToast("Error updating requirement.", false);
    }
  };

  // Delete requirement
  const handleDeleteRequirement = async (reqId) => {
    if (!window.confirm(`Are you sure you want to delete requirement ${reqId}?`)) return;
    try {
      const res = await axios.delete(`${API_BASE}/api/requirements/${reqId}`);
      if (res.data.success) {
        triggerToast("Requirement deleted successfully!");
        fetchRequirements();
      } else {
        triggerToast(res.data.error, false);
      }
    } catch (err) {
      triggerToast("Error deleting requirement.", false);
    }
  };

  // Open mapping modal
  const openMappingModal = async (req) => {
    setSelectedReq(req);
    setMappedTests([]);
    setShowMapModal(true);
    try {
      const res = await axios.get(`${API_BASE}/api/requirements/${req.req_id}/maps`);
      if (res.data.success) {
        setMappedTests(res.data.test_cases);
      }
    } catch (err) {
      console.error("Error fetching test maps:", err);
    }
  };

  // Save mapping
  const handleSaveMapping = async () => {
    if (!selectedReq) return;
    try {
      const res = await axios.post(`${API_BASE}/api/requirements/${selectedReq.req_id}/maps`, {
        test_cases: mappedTests
      });
      if (res.data.success) {
        triggerToast("Linked test scripts successfully!");
        setShowMapModal(false);
        setSelectedReq(null);
        fetchRequirements();
      } else {
        triggerToast(res.data.error, false);
      }
    } catch (err) {
      triggerToast("Error linking test scripts.", false);
    }
  };

  const toggleTestCheckbox = (filename) => {
    setMappedTests(prev =>
      prev.includes(filename) ? prev.filter(f => f !== filename) : [...prev, filename]
    );
  };

  // Stats computation
  const totalRules = requirements.length;
  const verifiedRules = requirements.filter(r => r.status === "Passed").length;
  const failedRules = requirements.filter(r => r.status === "Failed").length;
  const unverifiedRules = requirements.filter(r => r.status === "Untested").length;
  const coveragePercent = totalRules > 0 ? Math.round(((totalRules - unverifiedRules) / totalRules) * 100) : 0;

  // Active Project test cases list
  const availableTestCases = testCaseRepo[activeRepoProject] || [];

  return (
    <div style={{ padding: "10px 0" }}>
      {/* Messages */}
      {successMsg && (
        <div style={{ position: "fixed", top: "20px", right: "20px", zIndex: 10001, backgroundColor: "#10b981", color: "#ffffff", padding: "12px 20px", borderRadius: "8px", boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.2)", fontSize: "13px", fontWeight: "600" }}>
          ✓ {successMsg}
        </div>
      )}
      {errorMsg && (
        <div style={{ position: "fixed", top: "20px", right: "20px", zIndex: 10001, backgroundColor: "#ef4444", color: "#ffffff", padding: "12px 20px", borderRadius: "8px", boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.2)", fontSize: "13px", fontWeight: "600" }}>
          ❌ {errorMsg}
        </div>
      )}

      {/* Header and Filter */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", flexWrap: "wrap", gap: "15px" }}>
        <div style={{ textAlign: "left" }}>
          <h1 style={{ fontFamily: "var(--font-display)", fontSize: "24px", fontWeight: "700", margin: 0 }}>Business Requirements</h1>
          <p style={{ color: "var(--text-muted)", margin: "5px 0 0 0", fontSize: "14px" }}>Manage project-specific requirements, map them to automation tests, and view live compliance rates.</p>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <input
            type="text"
            placeholder="Search requirements..."
            className="gen-input"
            style={{ width: "240px", height: "40px", paddingLeft: "12px", boxSizing: "border-box", borderRadius: "8px", border: "1.5px solid #cbd5e1" }}
            value={searchRequirementsQuery}
            onChange={(e) => setSearchRequirementsQuery(e.target.value)}
          />
          <select
            value={activeRepoProject || ""}
            onChange={(e) => setActiveRepoProject(e.target.value)}
            className="gen-select"
            style={{ height: "40px", padding: "0 14px", minWidth: "160px" }}
          >
            {Object.keys(testCaseRepo || {}).map(proj => (
              <option key={proj} value={proj}>{proj} Project</option>
            ))}
          </select>

          <button
            className="btn-primary"
            style={{ margin: 0, padding: "10px 18px", height: "40px", background: "var(--primary)" }}
            onClick={() => setShowAddModal(true)}
          >
            + Add Requirement
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "20px", marginBottom: "24px" }}>
        <div style={{ background: "#f0f9ff", borderRadius: "12px", padding: "20px", color: "#0C4A6E", border: "1px solid #bae6fd", textAlign: "left" }}>
          <div style={{ fontSize: "11px", fontWeight: "700", textTransform: "uppercase", color: "#0369A1", letterSpacing: "0.5px" }}>Total Requirements</div>
          <div style={{ fontSize: "28px", fontWeight: "800", marginTop: "8px" }}>{totalRules}</div>
        </div>

        <div style={{ background: "#ecfdf5", borderRadius: "12px", padding: "20px", color: "#064E3B", border: "1px solid #a7f3d0", textAlign: "left" }}>
          <div style={{ fontSize: "11px", fontWeight: "700", textTransform: "uppercase", color: "#047857", letterSpacing: "0.5px" }}>Verified Rules</div>
          <div style={{ fontSize: "28px", fontWeight: "800", marginTop: "8px" }}>{verifiedRules}</div>
        </div>

        <div style={{ background: "#fef2f2", borderRadius: "12px", padding: "20px", color: "#881337", border: "1px solid #fecaca", textAlign: "left" }}>
          <div style={{ fontSize: "11px", fontWeight: "700", textTransform: "uppercase", color: "#BE123C", letterSpacing: "0.5px" }}>Failing Rules</div>
          <div style={{ fontSize: "28px", fontWeight: "800", marginTop: "8px" }}>{failedRules}</div>
        </div>

        <div style={{ background: "#fff7ed", borderRadius: "12px", padding: "20px", color: "#7c2d12", border: "1px solid #fed7aa", textAlign: "left" }}>
          <div style={{ fontSize: "11px", fontWeight: "700", textTransform: "uppercase", color: "#c2410c", letterSpacing: "0.5px" }}>Verification Coverage</div>
          <div style={{ fontSize: "28px", fontWeight: "800", marginTop: "8px" }}>{coveragePercent}%</div>
        </div>
      </div>

      {/* Main List */}
      <div className="table-card" style={{ padding: "24px" }}>
        <h3 className="panel-header-title" style={{ textAlign: "left", marginBottom: "16px" }}>Traceability Matrix</h3>

        <table className="projects-table">
          <thead>
            <tr>
              <th onClick={() => handleSort("req_id")} style={{ width: "100px", cursor: "pointer", userSelect: "none" }}>Req ID{renderSortIndicator("req_id")}</th>
              <th onClick={() => handleSort("title")} style={{ width: "200px", cursor: "pointer", userSelect: "none" }}>Rule Title{renderSortIndicator("title")}</th>
              <th onClick={() => handleSort("description")} style={{ cursor: "pointer", userSelect: "none" }}>Description{renderSortIndicator("description")}</th>
              <th onClick={() => handleSort("priority")} style={{ width: "100px", cursor: "pointer", userSelect: "none" }}>Priority{renderSortIndicator("priority")}</th>
              <th onClick={() => handleSort("status")} style={{ width: "120px", cursor: "pointer", userSelect: "none" }}>Coverage status{renderSortIndicator("status")}</th>
              <th onClick={() => handleSort("mapped_tests")} style={{ width: "150px", cursor: "pointer", userSelect: "none" }}>Mapped Tests{renderSortIndicator("mapped_tests")}</th>
              <th style={{ width: "180px" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" style={{ textAlign: "center", padding: "30px", color: "var(--text-muted)" }}>
                  <span className="ring-loader" style={{ display: "inline-block", marginRight: "10px" }}></span> Loading requirements...
                </td>
              </tr>
            ) : requirements.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ textAlign: "center", padding: "30px", color: "var(--text-muted)" }}>
                  No requirements captured for project <strong>{activeRepoProject}</strong>. Click "+ Add Requirement" above to add one.
                </td>
              </tr>
            ) : (
              getSortedRequirements().map((req) => {
                let badgeBg = "#f1f5f9";
                let badgeColor = "#475569";
                if (req.status === "Passed") {
                  badgeBg = "#ecfdf5";
                  badgeColor = "#10b981";
                } else if (req.status === "Failed") {
                  badgeBg = "#fef2f2";
                  badgeColor = "#ef4444";
                } else if (req.status === "Running") {
                  badgeBg = "#eff6ff";
                  badgeColor = "#3b82f6";
                }

                return (
                  <tr key={req.req_id}>
                    <td style={{ fontWeight: "700", fontFamily: "monospace" }}>{req.req_id}</td>
                    <td style={{ fontWeight: "600", textAlign: "left" }}>{req.title}</td>
                    <td style={{ textAlign: "left", color: "var(--text-muted)", fontSize: "13px" }}>{req.description}</td>
                    <td>
                      <span style={{ fontSize: "11px", fontWeight: "700", padding: "2px 6px", borderRadius: "4px", backgroundColor: req.priority === "High" ? "#fee2e2" : req.priority === "Medium" ? "#fef3c7" : "#f3f4f6", color: req.priority === "High" ? "#ef4444" : req.priority === "Medium" ? "#b45309" : "#4b5563" }}>
                        {req.priority}
                      </span>
                    </td>
                    <td>
                      <span className="table-status-badge" style={{ backgroundColor: badgeBg, color: badgeColor, fontWeight: "700" }}>
                        {req.status}
                      </span>
                    </td>
                    <td style={{ fontSize: "12px", color: "var(--text-muted)" }}>
                      {req.mapped_tests && req.mapped_tests.length > 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: "3px", alignItems: "center" }}>
                          <span style={{ fontWeight: "600", color: "var(--primary)" }}>{req.mapped_tests.length} Scripts</span>
                          <span style={{ fontSize: "10px", opacity: 0.8 }} title={req.mapped_tests.join(", ")}>
                            ({req.mapped_tests[0].replace(".json", "")}{req.mapped_tests.length > 1 ? ` +${req.mapped_tests.length - 1} more` : ""})
                          </span>
                        </div>
                      ) : (
                        <span style={{ color: "#94a3b8", fontStyle: "italic" }}>None linked</span>
                      )}
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                        <button
                          onClick={() => openMappingModal(req)}
                          style={{ background: "rgba(59, 130, 246, 0.08)", border: "1px solid rgba(59, 130, 246, 0.2)", borderRadius: "6px", color: "#3b82f6", fontWeight: "700", cursor: "pointer", padding: "4px 8px", fontSize: "11px" }}
                        >
                          <span style={{ display: "inline-flex", alignItems: "center", gap: "5px" }}><Link2 size={12} /> Link Tests</span>
                        </button>
                        <button
                          onClick={() => {
                            setSelectedReq(req);
                            setReqTitle(req.title);
                            setReqDesc(req.description || "");
                            setReqPriority(req.priority || "Medium");
                            setShowEditModal(true);
                          }}
                          style={{ background: "rgba(245, 158, 11, 0.08)", border: "1px solid rgba(245, 158, 11, 0.2)", borderRadius: "6px", color: "#d97706", fontWeight: "700", cursor: "pointer", padding: "4px 8px", fontSize: "11px" }}
                        >
                          ✏ Edit
                        </button>
                        <button
                          onClick={() => handleDeleteRequirement(req.req_id)}
                          style={{ background: "rgba(239, 68, 68, 0.08)", border: "1px solid rgba(239, 68, 68, 0.2)", borderRadius: "6px", color: "#ef4444", fontWeight: "700", cursor: "pointer", padding: "4px 8px", fontSize: "11px" }}
                        >
                          🗑 Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* --- ADD MODAL --- */}
      {showAddModal && (
        <div className="modal-overlay" style={{ zIndex: 10000 }}>
          <div className="modal-card" style={{ maxWidth: "480px", width: "100%" }}>
            <h3 style={{ margin: "0 0 16px 0", color: "#0f172a", fontSize: "18px", fontWeight: "700", textAlign: "left" }}>Add Business Requirement</h3>

            <form onSubmit={handleAddRequirement} style={{ display: "flex", flexDirection: "column", gap: "16px", textAlign: "left" }}>
              <div>
                <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#475569", marginBottom: "6px" }}>Rule Title</label>
                <input
                  type="text"
                  placeholder="e.g. Unique email constraint validation on signup"
                  value={reqTitle}
                  onChange={(e) => setReqTitle(e.target.value)}
                  style={{ width: "100%", padding: "10px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "13px" }}
                  required
                />
              </div>

              <div>
                <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#475569", marginBottom: "6px" }}>Description</label>
                <textarea
                  placeholder="Describe the business rules, inputs, constraints, expected behaviors..."
                  value={reqDesc}
                  onChange={(e) => setReqDesc(e.target.value)}
                  style={{ width: "100%", minHeight: "80px", padding: "10px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "13px", fontFamily: "inherit", resize: "vertical" }}
                />
              </div>

              <div>
                <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#475569", marginBottom: "6px" }}>Rule Priority</label>
                <select
                  value={reqPriority}
                  onChange={(e) => setReqPriority(e.target.value)}
                  style={{ width: "100%", padding: "10px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "13px", backgroundColor: "#ffffff" }}
                >
                  <option value="High">High Policy Rule</option>
                  <option value="Medium">🟡 Medium Policy Rule</option>
                  <option value="Low">Low Policy Rule</option>
                </select>
              </div>

              <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", marginTop: "10px" }}>
                <button
                  type="button"
                  className="btn-secondary"
                  style={{ margin: 0, padding: "8px 16px", height: "38px" }}
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  style={{ margin: 0, padding: "8px 16px", height: "38px", background: "var(--primary)" }}
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* --- EDIT MODAL --- */}
      {showEditModal && (
        <div className="modal-overlay" style={{ zIndex: 10000 }}>
          <div className="modal-card" style={{ maxWidth: "480px", width: "100%" }}>
            <h3 style={{ margin: "0 0 16px 0", color: "#0f172a", fontSize: "18px", fontWeight: "700", textAlign: "left" }}>Edit Requirement: {selectedReq?.req_id}</h3>

            <form onSubmit={handleEditRequirement} style={{ display: "flex", flexDirection: "column", gap: "16px", textAlign: "left" }}>
              <div>
                <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#475569", marginBottom: "6px" }}>Rule Title</label>
                <input
                  type="text"
                  value={reqTitle}
                  onChange={(e) => setReqTitle(e.target.value)}
                  style={{ width: "100%", padding: "10px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "13px" }}
                  required
                />
              </div>

              <div>
                <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#475569", marginBottom: "6px" }}>Description</label>
                <textarea
                  value={reqDesc}
                  onChange={(e) => setReqDesc(e.target.value)}
                  style={{ width: "100%", minHeight: "80px", padding: "10px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "13px", fontFamily: "inherit", resize: "vertical" }}
                />
              </div>

              <div>
                <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#475569", marginBottom: "6px" }}>Rule Priority</label>
                <select
                  value={reqPriority}
                  onChange={(e) => setReqPriority(e.target.value)}
                  style={{ width: "100%", padding: "10px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "13px", backgroundColor: "#ffffff" }}
                >
                  <option value="High">High Policy Rule</option>
                  <option value="Medium">🟡 Medium Policy Rule</option>
                  <option value="Low">Low Policy Rule</option>
                </select>
              </div>

              <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", marginTop: "10px" }}>
                <button
                  type="button"
                  className="btn-secondary"
                  style={{ margin: 0, padding: "8px 16px", height: "38px" }}
                  onClick={() => { setShowEditModal(false); setSelectedReq(null); }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  style={{ margin: 0, padding: "8px 16px", height: "38px", background: "var(--primary)" }}
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* --- LINK/MAP TESTS MODAL --- */}
      {showMapModal && (
        <div className="modal-overlay" style={{ zIndex: 10000 }}>
          <div className="modal-card" style={{ maxWidth: "540px", width: "100%" }}>
            <h3 style={{ margin: "0 0 6px 0", color: "#0f172a", fontSize: "18px", fontWeight: "700", textAlign: "left" }}>Link Test Scripts</h3>
            <p style={{ margin: "0 0 16px 0", fontSize: "12.5px", color: "var(--text-muted)", textAlign: "left" }}>
              Select which automated test scripts cover requirement <strong>{selectedReq?.req_id}</strong>:
            </p>

            {/* List scrollbox */}
            <div style={{ flex: 1, overflowY: "auto", border: "1px solid #cbd5e1", borderRadius: "6px", padding: "12px", textAlign: "left", display: "flex", flexDirection: "column", gap: "10px", minHeight: "150px" }}>
              {availableTestCases.length === 0 ? (
                <div style={{ color: "#94a3b8", textAlign: "center", padding: "20px" }}>
                  No automated test cases found in project "{activeRepoProject}". Go to Generator page to create one!
                </div>
              ) : (
                availableTestCases.map(tc => {
                  const isChecked = mappedTests.includes(tc.filename);
                  return (
                    <label
                      key={tc.filename}
                      style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px 10px", borderRadius: "6px", backgroundColor: isChecked ? "#f0fdf4" : "transparent", border: isChecked ? "1px solid #bbf7d0" : "1px solid #e2e8f0", cursor: "pointer", transition: "all 0.1s" }}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleTestCheckbox(tc.filename)}
                        style={{ cursor: "pointer", width: "16px", height: "16px" }}
                      />
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <span style={{ fontSize: "13px", fontWeight: "600", color: "#1e293b" }}>{tc.name}</span>
                        <span style={{ fontSize: "11px", color: "#64748b", fontFamily: "monospace" }}>{tc.filename}</span>
                      </div>
                    </label>
                  );
                })
              )}
            </div>

            <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", marginTop: "16px" }}>
              <button
                className="btn-secondary"
                style={{ margin: 0, padding: "8px 16px", height: "38px" }}
                onClick={() => { setShowMapModal(false); setSelectedReq(null); }}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                style={{ margin: 0, padding: "8px 16px", height: "38px", background: "var(--primary)" }}
                onClick={handleSaveMapping}
              >
                Save Mappings
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
